import { GlobalState } from 'store'
import { EMPTY, Observable, of } from 'rxjs'
import { buffer, debounceTime, mergeMap } from 'rxjs/operators'
import { combineEpics, ofType, StateObservable } from 'redux-observable'
import { flatten } from 'lodash-es'
import {
  AxeSubscriptionIdentifier,
  clientActionSubscribeBatch,
  ClientActionTypes,
  ClientSubscribeAction,
  ClientSubscribeBatchAction,
  ClientSubscribeMessage,
  ClientUnsubscribeAction,
  DealHistorySubscriptionIdentifier,
  DealSubscriptionIdentifier,
  EntityStreamSnapshotsEndAction,
  EntitySubscriptionMeta,
  EntitySubscriptionMetaAction,
  EntityTypeEnum,
  HistoricalQueryResponseAction,
  HistoricalQueryResult,
  NegotiationHistorySubscriptionIdentifier,
  NegotiationSubscriptionIdentifier,
  OrderSubscriptionIdentifier,
  PreDefinedFilter,
  ServerActionTypes,
  ServerMessageType,
  SubscribeType,
  SubscriptionFilter,
  SubscriptionIdentifier
} from 'api'
import { SelectionActions } from 'store/selection'
import {
  DefaultAxeSubscriptionFilters,
  DefaultDealHistorySubscriptionFilters,
  DefaultDealSubscriptionFilters,
  DefaultNegotiationHistorySubscriptionFilters,
  DefaultNegotiationSubscriptionFilters,
  DefaultOrderSubscriptionFilters,
  EntitySubscriptionFilters,
  PostTransformSubscriptionFilters,
  SubscriptionFilterConfig,
  updateWithStateFilters
} from 'model'
import { userGlobalGridSettingsSelector } from '../selectors/gridSelectors'
import { createAuthorisedUserSelector } from 'selectors/connectionSelectors'
import { isCurrentOrganisationBuySideSelector, isCurrentOrganisationSellSideSelector } from 'selectors/organisationSelectors'
import { NotificationActions, NotificationType } from '../store/notification'
import { Locale, Translation } from '../locale'
import { permissionedRoutesHaveEntityTypeSelector } from 'view/routes/routeSelectors'
import { BTQAction } from 'epics'
import { getPageSize } from 'utils/pageSizeUtils'

function getSubscribeType(subscriptionIdentifier: SubscriptionIdentifier) {
  const subscriptionIdToType: Record<SubscriptionIdentifier, SubscribeType> = {
    [SubscriptionIdentifier.STAGING]: SubscribeType.FILTERED,
    [SubscriptionIdentifier.BLOTTER]: SubscribeType.PAGE,
    [SubscriptionIdentifier.DEAL_INTEREST_PERIOD]: SubscribeType.FILTERED,
    [SubscriptionIdentifier.DEAL_HISTORY]: SubscribeType.FILTERED,
    [SubscriptionIdentifier.HISTORICAL]: SubscribeType.PAGE,
    [SubscriptionIdentifier.HISTORICAL_MID_TRADE_EVENTS]: SubscribeType.PAGE,
    [SubscriptionIdentifier.HISTORY]: SubscribeType.FILTERED,
    [SubscriptionIdentifier.INCOMING]: SubscribeType.PAGE,
    [SubscriptionIdentifier.INCOMING_TERMINATED_NOTIFICATION]: SubscribeType.LIVE_ONLY,
    [SubscriptionIdentifier.INITIATED]: SubscribeType.PAGE,
    [SubscriptionIdentifier.INITIATED_GRID_VIEW]: SubscribeType.FILTERED,
    [SubscriptionIdentifier.INITIATED_TERMINATED_NOTIFICATION]: SubscribeType.LIVE_ONLY,
    [SubscriptionIdentifier.LATEST]: SubscribeType.PAGE,
    [SubscriptionIdentifier.MTE_UPLOAD]: SubscribeType.FILTERED,
    [SubscriptionIdentifier.MID_TRADE_EVENT]: SubscribeType.FILTERED,
    [SubscriptionIdentifier.OPEN_REPO]: SubscribeType.PAGE,
    [SubscriptionIdentifier.STRUCTURED]: SubscribeType.PAGE,
    [SubscriptionIdentifier.AXE_TODAY_RESPONDER]: SubscribeType.PAGE,
    [SubscriptionIdentifier.AXE_TODAY_INITIATOR]: SubscribeType.PAGE,
    [SubscriptionIdentifier.ROLLS]: SubscribeType.PAGE,
    [SubscriptionIdentifier.COLLATERAL_DETAILS]: SubscribeType.FILTERED
  }

  return subscriptionIdToType[subscriptionIdentifier]
}

function getPreDefinedFilter(subscriptionIdentifier: SubscriptionIdentifier) {
  const subscriptionIdToType: Record<SubscriptionIdentifier, PreDefinedFilter | null> = {
    [SubscriptionIdentifier.STAGING]: null,
    [SubscriptionIdentifier.BLOTTER]: null,
    [SubscriptionIdentifier.DEAL_INTEREST_PERIOD]: null,
    [SubscriptionIdentifier.DEAL_HISTORY]: null,
    [SubscriptionIdentifier.HISTORICAL]: null,
    [SubscriptionIdentifier.HISTORICAL_MID_TRADE_EVENTS]: null,
    [SubscriptionIdentifier.HISTORY]: null,
    [SubscriptionIdentifier.INCOMING]: null,
    [SubscriptionIdentifier.INCOMING_TERMINATED_NOTIFICATION]: null,
    [SubscriptionIdentifier.INITIATED]: null,
    [SubscriptionIdentifier.INITIATED_GRID_VIEW]: PreDefinedFilter.NEGOTIATION_GRID_VIEW,
    [SubscriptionIdentifier.INITIATED_TERMINATED_NOTIFICATION]: null,
    [SubscriptionIdentifier.LATEST]: null,
    [SubscriptionIdentifier.MTE_UPLOAD]: null,
    [SubscriptionIdentifier.MID_TRADE_EVENT]: null,
    [SubscriptionIdentifier.OPEN_REPO]: null,
    [SubscriptionIdentifier.STRUCTURED]: null,
    [SubscriptionIdentifier.AXE_TODAY_RESPONDER]: null,
    [SubscriptionIdentifier.AXE_TODAY_INITIATOR]: null,
    [SubscriptionIdentifier.ROLLS]: null,
    [SubscriptionIdentifier.COLLATERAL_DETAILS]: null
  }

  return subscriptionIdToType[subscriptionIdentifier]
}

function createSubscribeMessages(
  entityTypeEnum: EntityTypeEnum,
  subscriptionFilters: EntitySubscriptionFilters
): Omit<ClientSubscribeMessage, 'messageWhenStateCorrupt'>[] {
  return Object.entries(subscriptionFilters)
    .filter(
      ([subscriptionIdentifier, filters]) => filters.length > 0 || !!getPreDefinedFilter(subscriptionIdentifier as SubscriptionIdentifier)
    )
    .map(([subscriptionIdentifier, filters]: [SubscriptionIdentifier, SubscriptionFilter[]]) => {
      const subscribeType = getSubscribeType(subscriptionIdentifier)
      const preDefinedFilter = getPreDefinedFilter(subscriptionIdentifier)
      const pageSize = getPageSize(subscriptionIdentifier)
      return {
        correlationId: '',
        entityTypeEnum,
        preDefinedFilter: preDefinedFilter,
        filters,
        postTransformFilters: PostTransformSubscriptionFilters[subscriptionIdentifier],
        subscribeType: subscribeType,
        subscriptionIdentifier,
        limitResults: false,
        pageNumber: subscribeType === SubscribeType.PAGE ? 0 : null,
        pageSize: subscribeType === SubscribeType.PAGE ? pageSize : null,
        summaryRequest: null
      }
    })
}

export function initialEntitySubscriptionEpic(
  action$: Observable<BTQAction>,
  state$: StateObservable<GlobalState>,
  _dependencies: {}
): Observable<BTQAction> {
  return action$.pipe(
    ofType<BTQAction, ServerActionTypes.ENTITY_STREAM_SNAPSHOTS_END, EntityStreamSnapshotsEndAction>(
      ServerActionTypes.ENTITY_STREAM_SNAPSHOTS_END
    ),
    mergeMap(() => {
      const isBuySideOrg = isCurrentOrganisationBuySideSelector(state$.value)
      const isSellSideOrg = isCurrentOrganisationSellSideSelector(state$.value)

      const filterConfig: SubscriptionFilterConfig = {
        globalGridSettings: userGlobalGridSettingsSelector(state$.value),
        authorisedUser: createAuthorisedUserSelector()(state$.value),
        isBuySide: isBuySideOrg
      }
      const entitySubscriptionEnabledForEntityType = permissionedRoutesHaveEntityTypeSelector(state$.value)

      const payload = [
        ...(!entitySubscriptionEnabledForEntityType(EntityTypeEnum.ORDER)
          ? []
          : createSubscribeMessages(EntityTypeEnum.ORDER, {
              ...DefaultOrderSubscriptionFilters,
              [OrderSubscriptionIdentifier.STAGING]: updateWithStateFilters(
                DefaultOrderSubscriptionFilters[OrderSubscriptionIdentifier.STAGING],
                OrderSubscriptionIdentifier.STAGING,
                filterConfig
              )
            })),
        ...(!entitySubscriptionEnabledForEntityType(EntityTypeEnum.DEAL)
          ? []
          : createSubscribeMessages(EntityTypeEnum.DEAL, {
              ...DefaultDealSubscriptionFilters,
              [DealSubscriptionIdentifier.BLOTTER]: updateWithStateFilters(
                DefaultDealSubscriptionFilters[DealSubscriptionIdentifier.BLOTTER],
                DealSubscriptionIdentifier.BLOTTER,
                filterConfig
              ),
              [DealSubscriptionIdentifier.OPEN_REPO]: updateWithStateFilters(
                DefaultDealSubscriptionFilters[DealSubscriptionIdentifier.OPEN_REPO],
                DealSubscriptionIdentifier.OPEN_REPO,
                filterConfig
              ),
              [DealSubscriptionIdentifier.ROLLS]: updateWithStateFilters(
                DefaultDealSubscriptionFilters[DealSubscriptionIdentifier.ROLLS],
                DealSubscriptionIdentifier.ROLLS,
                filterConfig
              ),
              [DealSubscriptionIdentifier.STRUCTURED]: updateWithStateFilters(
                DefaultDealSubscriptionFilters[DealSubscriptionIdentifier.STRUCTURED],
                DealSubscriptionIdentifier.STRUCTURED,
                filterConfig
              )
            })),
        ...(!entitySubscriptionEnabledForEntityType(EntityTypeEnum.DEAL_HISTORY)
          ? []
          : createSubscribeMessages(EntityTypeEnum.DEAL_HISTORY, {
              ...DefaultDealHistorySubscriptionFilters,
              [DealHistorySubscriptionIdentifier.HISTORICAL_MID_TRADE_EVENTS]: updateWithStateFilters(
                DefaultDealHistorySubscriptionFilters[DealHistorySubscriptionIdentifier.HISTORICAL_MID_TRADE_EVENTS],
                DealHistorySubscriptionIdentifier.HISTORICAL_MID_TRADE_EVENTS,
                filterConfig
              )
            })),
        ...(!entitySubscriptionEnabledForEntityType(EntityTypeEnum.NEGOTIATION)
          ? []
          : createSubscribeMessages(EntityTypeEnum.NEGOTIATION, {
              ...DefaultNegotiationSubscriptionFilters,
              [NegotiationSubscriptionIdentifier.INCOMING]: isSellSideOrg
                ? updateWithStateFilters(
                    DefaultNegotiationSubscriptionFilters[NegotiationSubscriptionIdentifier.INCOMING],
                    NegotiationSubscriptionIdentifier.INCOMING,
                    filterConfig
                  )
                : [],
              [NegotiationSubscriptionIdentifier.INITIATED]: isBuySideOrg
                ? updateWithStateFilters(
                    DefaultNegotiationSubscriptionFilters[NegotiationSubscriptionIdentifier.INITIATED],
                    NegotiationSubscriptionIdentifier.INITIATED,
                    filterConfig
                  )
                : [],
              [NegotiationSubscriptionIdentifier.INITIATED_GRID_VIEW]: isBuySideOrg
                ? updateWithStateFilters(
                    DefaultNegotiationSubscriptionFilters[NegotiationSubscriptionIdentifier.INITIATED_GRID_VIEW],
                    NegotiationSubscriptionIdentifier.INITIATED_GRID_VIEW,
                    filterConfig
                  )
                : [],
              [NegotiationSubscriptionIdentifier.INCOMING_TERMINATED_NOTIFICATION]: isSellSideOrg
                ? updateWithStateFilters(
                    DefaultNegotiationSubscriptionFilters[NegotiationSubscriptionIdentifier.INCOMING_TERMINATED_NOTIFICATION],
                    NegotiationSubscriptionIdentifier.INCOMING_TERMINATED_NOTIFICATION,
                    filterConfig
                  )
                : [],
              [NegotiationSubscriptionIdentifier.INITIATED_TERMINATED_NOTIFICATION]: isBuySideOrg
                ? updateWithStateFilters(
                    DefaultNegotiationSubscriptionFilters[NegotiationSubscriptionIdentifier.INITIATED_TERMINATED_NOTIFICATION],
                    NegotiationSubscriptionIdentifier.INITIATED_TERMINATED_NOTIFICATION,
                    filterConfig
                  )
                : []
            })),
        ...(!entitySubscriptionEnabledForEntityType(EntityTypeEnum.NEGOTIATION_HISTORY)
          ? []
          : createSubscribeMessages(EntityTypeEnum.NEGOTIATION_HISTORY, {
              ...DefaultNegotiationHistorySubscriptionFilters,
              [NegotiationHistorySubscriptionIdentifier.LATEST]: [],
              [NegotiationHistorySubscriptionIdentifier.HISTORICAL]: []
            })),
        ...(!entitySubscriptionEnabledForEntityType(EntityTypeEnum.AXE)
          ? []
          : createSubscribeMessages(EntityTypeEnum.AXE, {
              ...DefaultAxeSubscriptionFilters,
              [AxeSubscriptionIdentifier.AXE_TODAY_RESPONDER]: updateWithStateFilters(
                DefaultAxeSubscriptionFilters[AxeSubscriptionIdentifier.AXE_TODAY_RESPONDER],
                AxeSubscriptionIdentifier.AXE_TODAY_RESPONDER,
                filterConfig
              ),
              [AxeSubscriptionIdentifier.AXE_TODAY_INITIATOR]: updateWithStateFilters(
                DefaultAxeSubscriptionFilters[AxeSubscriptionIdentifier.AXE_TODAY_INITIATOR],
                AxeSubscriptionIdentifier.AXE_TODAY_INITIATOR,
                filterConfig
              )
            }))
      ]

      return payload.length > 0
        ? of(
            clientActionSubscribeBatch({
              payload,
              clearExistingSubscriptions: false
            })
          )
        : EMPTY
    })
  )
}

export function entitySubscribeEpic(
  action$: Observable<BTQAction>,
  _state$: StateObservable<GlobalState>,
  _dependencies: {}
): Observable<BTQAction> {
  return action$.pipe(
    ofType<BTQAction, ClientActionTypes.SUBSCRIBE_MESSAGE, ClientSubscribeAction>(ClientActionTypes.SUBSCRIBE_MESSAGE),
    mergeMap(action => {
      const actions: BTQAction[] = [
        SelectionActions.addEntitySubscription(action.payload.entityTypeEnum, action.payload.subscriptionIdentifier, action.payload.filters)
      ]
      if (
        action.payload.entityTypeEnum === EntityTypeEnum.NEGOTIATION_HISTORY &&
        action.payload.subscriptionIdentifier === NegotiationHistorySubscriptionIdentifier.LATEST
      ) {
        actions.push(SelectionActions.clearAuditSubscriptions())
      }

      return of(...actions)
    })
  )
}

export function entitySubscribeBatchEpic(
  action$: Observable<BTQAction>,
  _state$: StateObservable<GlobalState>,
  _dependencies: {}
): Observable<BTQAction> {
  return action$.pipe(
    ofType<BTQAction, ClientActionTypes.SUBSCRIBE_BATCH_MESSAGE, ClientSubscribeBatchAction>(ClientActionTypes.SUBSCRIBE_BATCH_MESSAGE),
    mergeMap(action =>
      of(
        ...action.payload.payload.map(subscribeMessage =>
          SelectionActions.addEntitySubscription(
            subscribeMessage.entityTypeEnum,
            subscribeMessage.subscriptionIdentifier as SubscriptionIdentifier,
            subscribeMessage.filters
          )
        )
      )
    )
  )
}

export function entityUnsubscribeEpic(
  action$: Observable<BTQAction>,
  _state$: StateObservable<GlobalState>,
  _dependencies: {}
): Observable<BTQAction> {
  return action$.pipe(
    ofType<BTQAction, ClientActionTypes.UNSUBSCRIBE_MESSAGE, ClientUnsubscribeAction>(ClientActionTypes.UNSUBSCRIBE_MESSAGE),
    mergeMap(action => {
      const actions: BTQAction[] = [
        SelectionActions.removeEntitySubscription(action.payload.entityTypeEnum, action.payload.subscriptionIdentifier)
      ]
      if (
        action.payload.entityTypeEnum === EntityTypeEnum.NEGOTIATION_HISTORY &&
        action.payload.subscriptionIdentifier === NegotiationHistorySubscriptionIdentifier.LATEST
      ) {
        actions.push(SelectionActions.clearAuditSubscriptions())
      }

      return of(...actions)
    })
  )
}

const getNonSuccessfulQueryResponseErrorMessage = (responseAction: HistoricalQueryResponseAction) => {
  const { result, subscriptionIdentifier, entityTypeEnum } = responseAction.payload

  switch (result) {
    case HistoricalQueryResult.FAILED_ERROR:
    case HistoricalQueryResult.FAILED_NOT_READY:
    case HistoricalQueryResult.NO_REPLY_FROM_CLUSTER:
      return Locale.translate(Translation.SC_SUBSCRIPTION_RESPONSE_ERROR, {
        subscriptionIdentifier,
        entityTypeEnum,
        error: result
      })
    case HistoricalQueryResult.FAILED_RESULT_SET_TOO_LARGE:
      return Locale.translate(Translation.SC_SUBSCRIPTION_RESPONSE_TOO_LARGE_ERROR, {
        subscriptionIdentifier,
        entityTypeEnum
      })
    default:
      throw new Error(`Unsupported HistoricalQueryResponseMessage - ${result}`)
  }
}

export function pageSubscriptionResponseReceivedEpic(
  action$: Observable<BTQAction>,
  _state$: StateObservable<GlobalState>,
  _dependencies: {}
): Observable<BTQAction> {
  return action$.pipe(
    ofType<BTQAction, ServerMessageType.HISTORICAL_QUERY_RESPONSE, HistoricalQueryResponseAction>(
      ServerMessageType.HISTORICAL_QUERY_RESPONSE
    ),
    mergeMap(action => {
      if (action.payload.result !== HistoricalQueryResult.SUCCESS) {
        return of(
          NotificationActions.showGrowl({
            type: NotificationType.Error,
            messageText: getNonSuccessfulQueryResponseErrorMessage(action)
          })
        )
      }

      if (action.payload.page !== null || action.payload.totalPages !== null) {
        return of(
          SelectionActions.addSubscriptionPageDetails(
            action.payload.entityTypeEnum,
            action.payload.subscriptionIdentifier as SubscriptionIdentifier,
            action.payload.page!,
            action.payload.totalPages!,
            action.payload.resultCount,
            action.payload.totalResultCount
          )
        )
      }

      return EMPTY
    })
  )
}

export function entitySubscriptionMetaReceivedEpic(
  action$: Observable<BTQAction>,
  _state$: StateObservable<GlobalState>,
  _dependencies: {}
): Observable<BTQAction> {
  const subscriptionMetaAction$ = action$.pipe(
    ofType<BTQAction, ServerMessageType.ENTITY_SUBSCRIPTION_META, EntitySubscriptionMetaAction>(ServerMessageType.ENTITY_SUBSCRIPTION_META)
  )

  return subscriptionMetaAction$.pipe(
    buffer(subscriptionMetaAction$.pipe(debounceTime(250))),
    mergeMap(subscriptionMetaActions => {
      const subscriptionMetaMap = subscriptionMetaActions
        .map(subscriptionMetaAction => subscriptionMetaAction.payload)
        .reduce<{ [x in EntityTypeEnum]?: { [y in SubscriptionIdentifier]?: EntitySubscriptionMeta } }>((acc, subscriptionMetaMessage) => {
          acc[subscriptionMetaMessage.entityType] = {
            ...acc[subscriptionMetaMessage.entityType],
            ...subscriptionMetaMessage.subscriptionMetaMap
          }
          return acc
        }, {})

      const actions: BTQAction[] = flatten(
        Object.keys(subscriptionMetaMap).map((entityType: EntityTypeEnum) =>
          Object.keys(subscriptionMetaMap[entityType]!).map((subscriptionIdentifier: SubscriptionIdentifier) =>
            SelectionActions.editSubscriptionPageDetails(
              entityType,
              subscriptionIdentifier,
              subscriptionMetaMap[entityType]![subscriptionIdentifier]!.pageNumber,
              subscriptionMetaMap[entityType]![subscriptionIdentifier]!.totalPages
            )
          )
        )
      )

      return of(...actions)
    })
  )
}

export const entitySubscriptionEpic = combineEpics(
  initialEntitySubscriptionEpic,
  entitySubscribeEpic,
  entitySubscribeBatchEpic,
  entityUnsubscribeEpic,
  pageSubscriptionResponseReceivedEpic,
  entitySubscriptionMetaReceivedEpic
)
